// Dev-only artifact fixtures + their dispatcher, split out of `mocks.ts` on the
// `dashboard-mocks` precedent. Absent entirely until now, so `/org/:org/artifacts`
// rendered `<ErrorState/>` under `PUBLIC_MOCKS` — the whole screen below the
// heading was an error box.
//
// THE FILTER IS HONOURED, not ignored. `useArtifacts` puts `run_id`/`task_id`/
// `repo_id` in the query KEY precisely so narrowing re-reads server-side, and a
// mock that served the same rows whichever way the filter bar was set would make
// the one affordance this screen offers against truncation look broken.
//
// NO CURSOR — `GET /v1/artifacts` is a bounded head keyed `artifacts`, not the
// `{data, next_cursor}` envelope (`apps/api/src/routes/artifacts.ts`). Answering
// the canonical envelope here would teach the client a paging story the route
// mints no token for.

import type { ApiArtifact } from './artifacts';
import { at } from './mock-session';
import { unhandledMockRoute } from './unhandled-mock-route';

/**
 * `created_by` is `run:<id>` or `user:<tokenId>` — the two shapes the server
 * emits, one of each, so the row's provenance line has both cases to render.
 */
const artifacts: ApiArtifact[] = [
  {
    id: 'art_mock01',
    repo_id: 'repo_1',
    run_id: 'run_mock01',
    task_id: 'tsk_mock_live',
    content_type: 'image/png',
    size: 184_320,
    sha256: 'a'.repeat(64),
    created_by: 'run:run_mock01',
    visibility: 'run',
    expires_iso: at('2026-07-24'),
    created_iso: at('2026-07-17'),
    updated_iso: at('2026-07-17'),
  },
  {
    // Already shared: the ONE row that shows the terminal state, since no surface
    // un-shares an artifact and the story must be able to see a link that exists.
    id: 'art_mock02',
    repo_id: 'repo_1',
    run_id: null,
    task_id: null,
    content_type: 'text/csv',
    size: 2_048,
    sha256: 'b'.repeat(64),
    created_by: 'user:tok_mock01',
    visibility: 'public-link',
    expires_iso: null,
    created_iso: at('2026-07-12'),
    updated_iso: at('2026-07-16'),
  },
  {
    // Bytes never landed / size unknown — the nullable trio, so the table's
    // fallbacks are reachable rather than dead branches behind a full fixture.
    id: 'art_mock03',
    repo_id: null,
    run_id: 'run_mock_done',
    task_id: null,
    content_type: 'application/octet-stream',
    size: null,
    sha256: null,
    created_by: 'run:run_mock_done',
    visibility: 'account',
    expires_iso: null,
    created_iso: at('2026-07-05'),
    updated_iso: at('2026-07-05'),
  },
];

/** A presigned GET is a CREDENTIAL — short-lived, and the screen states the expiry. */
const access = (row: ApiArtifact, visibility: ApiArtifact['visibility']): unknown => ({
  ...row,
  visibility,
  url: `https://r2.example/artifacts/${row.id}?sig=devmock`,
  expires_in_seconds: 900,
});

/** Apply the route's own three narrowings, so the filter bar visibly does something. */
function narrow(query: string): ApiArtifact[] {
  const params = new URLSearchParams(query);
  const at_ = (key: string): string | null => params.get(key);
  return artifacts.filter(
    (a) =>
      (at_('run_id') === null || a.run_id === at_('run_id')) &&
      (at_('task_id') === null || a.task_id === at_('task_id')) &&
      (at_('repo_id') === null || a.repo_id === at_('repo_id')),
  );
}

/**
 * Handle a `/v1/artifacts*` request, or return `null` if `path` is not an
 * artifact route (so `mockFetch` falls through). An unknown id REJECTS: the API
 * folds unknown / foreign / bytes-never-landed into one 404, and a mock that
 * presigned a URL for a row nobody has would hide that branch.
 */
export function artifactsMock(path: string, method: string): Promise<unknown> | null {
  const [bare = path, query = ''] = path.split('?');
  if (bare !== '/v1/artifacts' && !bare.startsWith('/v1/artifacts/')) return null;
  const reject = (): Promise<never> => unhandledMockRoute(method, path);

  if (bare === '/v1/artifacts') {
    // Keyed `artifacts`, NOT `{data, next_cursor}` — see the header.
    return method === 'GET' ? Promise.resolve({ artifacts: narrow(query) }) : reject();
  }

  const [id = '', action = ''] = bare.slice('/v1/artifacts/'.length).split('/');
  const row = artifacts.find((a) => a.id === id);
  if (row === undefined) return reject();
  if (action === 'url' && method === 'GET') return Promise.resolve(access(row, row.visibility));
  // Share is DURABLE and there is no way back, so the fixture flips in place: a
  // second read has to show `public-link`, or the story could re-share for ever.
  if (action === 'share' && method === 'POST') {
    artifacts[artifacts.indexOf(row)] = { ...row, visibility: 'public-link' };
    return Promise.resolve(access(row, 'public-link'));
  }
  if (action === '' && method === 'DELETE') {
    artifacts.splice(artifacts.indexOf(row), 1);
    // 204 with an EMPTY body on the wire; `apiFetch<void>` resolves `undefined`.
    return Promise.resolve(undefined);
  }
  return reject();
}

/** Restore the pristine fixtures — the shared reset seam (`./mock-state.ts`). */
const PRISTINE_ARTIFACTS = artifacts.map((row) => structuredClone(row));

export function resetArtifactsMocks(): void {
  artifacts.splice(0, artifacts.length, ...PRISTINE_ARTIFACTS.map((row) => structuredClone(row)));
}
